import { api } from '../client';

import type {
  Broker,
  BrokerChannelType,
  BrokerOnboardingInput,
  BrokerRegistrationCheck,
} from './brokers';

/**
 * Triagem do cadastro do corretor na SUSEP e cadastro publico de corretores com
 * acesso na hora (ADR 0095).
 */

/** Resultado da triagem. So `apta`, `nao_configurada` e `nao_consultada` deixam seguir. */
export type BrokerSusepStatus =
  | 'nao_configurada'
  | 'nao_consultada'
  | 'apta'
  | 'nao_encontrada'
  | 'situacao_irregular'
  | 'sem_danos'
  | 'indisponivel';

export interface BrokerSusepScreening {
  status: BrokerSusepStatus;
  /** O cadastro pode seguir. Falso para a corretora nao apta e a SUSEP fora do ar. */
  proceed: boolean;
  /** Motivo estavel da recusa: susep_nao_encontrada, susep_situacao, susep_sem_danos, susep_indisponivel. */
  reason: string;
  message: string;
  susep_number: string;
  situation: string;
  products: number[];
  damages_enabled: boolean;
  checked_at: string | null;
}

export interface BrokerSusepConsultation {
  broker: Broker;
  screening: BrokerSusepScreening;
  /** A ficha mudou. A SUSEP desligada ou fora do ar nao grava. */
  updated: boolean;
}

export const brokerSusep = {
  /** Consulta antecipada do cadastro simples e da troca de CNPJ. */
  check: (document: string, signal?: AbortSignal) =>
    api.get<BrokerSusepScreening>(
      `/api/v1/brokers/susep-check?document=${encodeURIComponent(document)}`,
      signal,
    ),
  /** "Consultar SUSEP" do detalhe da corretora. */
  consult: (brokerId: string) =>
    api.post<BrokerSusepConsultation>(`/api/v1/brokers/${brokerId}/susep-consultation`),
};

// --- Cadastro publico --------------------------------------------------------------

/** O endereco publico resolvido. Fechado, a tela mostra `message`. */
export interface BrokerPublicSignupPage {
  tenant_slug: string;
  tenant_name: string;
  available: boolean;
  message: string;
  options: {
    channels: BrokerChannelType[];
    roles: unknown[];
    banks: { code: string; name: string }[];
  };
  privacy_notice_version: string;
}

/** O passo Dados do Usuario: o administrador do portal da corretora, com a senha. */
export interface BrokerPublicSignupUser {
  name: string;
  email: string;
  cpf: string;
  phone: string;
  password: string;
  password_confirmation: string;
}

export interface BrokerPublicSignupInput {
  privacy_acknowledged: boolean;
  /** So a corretora: o cadastro publico cria um usuario so, o do passo Dados do Usuario. */
  draft: Pick<BrokerOnboardingInput, 'broker'>;
  user: BrokerPublicSignupUser;
}

export interface BrokerPublicSignupResult {
  broker_id: string;
  broker_number: number;
  legal_name: string;
  document: string;
  email: string;
  tenant_slug: string;
  /** `/entrar/{slug}/corretor`. */
  login_path: string;
  message: string;
  /** O acesso so vale depois do clique no link enviado ao e-mail (ADR 0096). */
  activation_required?: boolean;
  /** Para onde o link foi, sem o endereco inteiro. */
  email_masked?: string;
  activation_expires_at?: string;
  /** Falso: o link nao saiu, e a pessoa pede outro na tela de acesso. */
  activation_email_sent?: boolean;
}

function caminhoPublico(endereco: string): string {
  return `/api/v1/public/broker-signup/${encodeURIComponent(endereco)}`;
}

export const publicBrokerSignup = {
  page: (endereco: string, signal?: AbortSignal) =>
    api.get<BrokerPublicSignupPage>(caminhoPublico(endereco), signal),
  check: (endereco: string, document: string) =>
    api.post<BrokerRegistrationCheck>(
      `${caminhoPublico(endereco)}/check`,
      { document },
      { skipAuth: true },
    ),
  complete: (endereco: string, input: BrokerPublicSignupInput) =>
    api.post<BrokerPublicSignupResult>(
      `${caminhoPublico(endereco)}/completion`,
      input,
      {
        skipAuth: true,
      },
    ),
};
